import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, Users, MessageSquare, X, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
    listTodosCore,
    listRelationshipsCore,
    listEventsCore,
} from '@gv/core';
import { TodoItem } from '../lib/todos';

interface ContactTimelineModalProps {
    contactId: string;
    contactName: string;
    isOpen: boolean;
    onClose: () => void;
    onEventClick: (eventId: string) => void;
}

interface TimelineEvent {
    id: string;
    type: 'calendar' | 'relationship' | 'contact_event';
    title: string;
    date: string;
    location?: string;
    description?: string;
    avatar?: string;
    metadata?: any;
}

export default function ContactTimelineModal({
    contactId,
    contactName,
    isOpen,
    onClose,
    onEventClick
}: ContactTimelineModalProps) {
    const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        totalEvents: 0,
        upcomingMeetings: 0,
        lastInteraction: null as string | null
    });

    useEffect(() => {
        if (!isOpen || !contactId) return;
        loadContactTimeline();
    }, [isOpen, contactId]);

    const loadContactTimeline = async () => {
        setLoading(true);
        try {
            const events: TimelineEvent[] = [];

            // 1. Load calendar events (todos linked to this contact) via core
            const calendarTodos = await listTodosCore(supabase as any, { contactId });
            events.push(...calendarTodos.map((event: any): TimelineEvent => ({
                id: event.id,
                type: 'calendar',
                title: event.title,
                date: event.due_date || event.created_at || new Date().toISOString(),
                location: event.location || undefined,
                description: event.description || undefined,
                metadata: event
            })));

            // 2. Load relationship events (where this contact is involved)
            // 2. Load relationship links owned by this contact via core
            const relationships = await listRelationshipsCore(supabase as any, contactId);
            events.push(...relationships.map((rel: any): TimelineEvent => ({
                id: `rel_${rel.id}`,
                type: 'relationship',
                title: `Met ${rel.relatedName}`,
                date: rel.whenMet || new Date().toISOString(),
                location: rel.whereMet || undefined,
                description: `${rel.relation}${rel.notes ? ` - ${rel.notes}` : ''}`,
                avatar: rel.relatedAvatar || undefined,
                metadata: rel
            })));

            // 3. Load contact events (interaction logs)
            // 3. Load contact events via core
            const contactEvents = await listEventsCore(supabase as any, contactId);
            events.push(...contactEvents.map((event: any): TimelineEvent => ({
                id: `evt_${event.id}`,
                type: 'contact_event',
                title: event.name,
                date: event.when || event.created_at,
                location: event.where || undefined,
                description: event.notes || undefined,
                metadata: event
            })));

            // Sort all events by date (most recent first)
            const sortedEvents = events.sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );

            setTimelineEvents(sortedEvents);

            // Calculate stats
            const now = new Date();
            const upcoming = sortedEvents.filter(e =>
                e.type === 'calendar' && new Date(e.date) > now
            ).length;

            const lastInteraction = sortedEvents.length > 0
                ? sortedEvents[0].date
                : null;

            setStats({
                totalEvents: sortedEvents.length,
                upcomingMeetings: upcoming,
                lastInteraction
            });

        } catch (error) {
            console.error('Failed to load contact timeline:', error);
        } finally {
            setLoading(false);
        }
    };

    const getEventIcon = (type: string) => {
        switch (type) {
            case 'calendar': return <Calendar className="w-4 h-4 text-blue-400" />;
            case 'relationship': return <Users className="w-4 h-4 text-green-400" />;
            case 'contact_event': return <MessageSquare className="w-4 h-4 text-purple-400" />;
            default: return <Clock className="w-4 h-4 text-gray-400" />;
        }
    };

    const getEventTypeLabel = (type: string) => {
        switch (type) {
            case 'calendar': return 'Scheduled';
            case 'relationship': return 'Relationship';
            case 'contact_event': return 'Interaction';
            default: return 'Event';
        }
    };

    const formatDate = (date: string) => {
        const d = new Date(date);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - d.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return 'Yesterday';
        if (diffDays === 0) return 'Today';
        if (diffDays < 7) return `${diffDays} days ago`;

        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const isUpcoming = (date: string) => {
        return new Date(date) > new Date();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />

            <div className="relative w-full max-w-2xl mx-4 max-h-[80vh] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                    <div>
                        <h2 className="text-lg font-semibold text-white">
                            {contactName} Timeline
                        </h2>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                            <span>{stats.totalEvents} interactions</span>
                            {stats.upcomingMeetings > 0 && (
                                <span className="text-blue-400">{stats.upcomingMeetings} upcoming</span>
                            )}
                            {stats.lastInteraction && (
                                <span>Last: {formatDate(stats.lastInteraction)}</span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Timeline Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400">Loading timeline...</div>
                        </div>
                    ) : timelineEvents.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400 mb-2">No interactions found</div>
                            <div className="text-sm text-gray-500">
                                Schedule a meeting or log an interaction to start building this timeline
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {timelineEvents.map((event, index) => (
                                <div
                                    key={event.id}
                                    className={`relative flex gap-4 p-4 rounded-lg border transition-colors ${isUpcoming(event.date)
                                        ? 'border-blue-700 bg-blue-900/20'
                                        : 'border-gray-700 bg-gray-800 hover:bg-gray-750'
                                        } ${event.type === 'calendar' ? 'cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (event.type === 'calendar' && event.metadata) {
                                            onEventClick(event.metadata.id);
                                            onClose();
                                        }
                                    }}
                                >
                                    {/* Timeline line */}
                                    {index < timelineEvents.length - 1 && (
                                        <div className="absolute left-6 top-12 w-px h-full bg-gray-700" />
                                    )}

                                    {/* Icon */}
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center">
                                        {getEventIcon(event.type)}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <h3 className="font-medium text-gray-200 truncate">
                                                {event.title}
                                            </h3>
                                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                                <span className="px-2 py-1 rounded-full bg-gray-700 text-gray-300">
                                                    {getEventTypeLabel(event.type)}
                                                </span>
                                                {isUpcoming(event.date) && (
                                                    <span className="px-2 py-1 rounded-full bg-blue-700 text-blue-200">
                                                        Upcoming
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-sm text-gray-400 mb-2">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatDate(event.date)}
                                                    {isUpcoming(event.date) && (
                                                        <span className="ml-1">
                                                            at {new Date(event.date).toLocaleTimeString([], {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                                {event.location && (
                                                    <div className="flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {event.location}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {event.description && (
                                            <div className="text-sm text-gray-300">
                                                {event.description}
                                            </div>
                                        )}

                                        {event.type === 'calendar' && (
                                            <div className="flex items-center gap-1 text-xs text-blue-400 mt-2">
                                                <span>Click to view event</span>
                                                <ChevronRight className="w-3 h-3" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}